/**
 * Menu Tree Builder Utility
 *
 * Converts flat menu function array from backend into hierarchical tree structure
 */
import { logger } from './logger';

export interface MenuFunctionDto {
  nodeId: number;
  functionId: string | null;
  functionName: string | null;
  parentId: number | null;
  sequenceNo: number | null;
  programId: string;
  userId: string;
  lastUpdate: string;
}

export interface MenuTreeNode {
  key: string;
  nodeId: number;
  label: string;
  icon?: string;
  path?: string;
  order: number;
  children?: MenuTreeNode[];
}

/**
 * Icon mapping for menu functions
 */
const ICON_MAP: Record<string, string> = {
  DASHBOARD: 'DashboardOutlined',
  USERMGT: 'UserOutlined',
  ROLEMGT: 'SafetyOutlined',
  INVENTORY: 'ShoppingOutlined',
  SALES: 'LineChartOutlined',
  REPORTS: 'FileTextOutlined',
  SETTINGS: 'SettingOutlined',
  SYSTEM: 'ToolOutlined',
  MASTER: 'DatabaseOutlined',
  CUSTOMER: 'TeamOutlined',
  SUPPLIER: 'ShopOutlined',
  JOURNAL: 'BookOutlined',
  COA: 'ApartmentOutlined',
  GLAPULL: 'CloudDownloadOutlined',
  GLAPUSH: 'CloudUploadOutlined',
  EXCHANGE: 'SwapOutlined',
  TRADE: 'TransactionOutlined',
  CORRESPONDENT: 'GlobalOutlined',
  CURRENCY: 'DollarOutlined',
};

/**
 * Path mapping for menu functions
 * IMPORTANT: These paths must match routes defined in routes.tsx
 *
 * Note: Folder items (MASTER, GLAPULL, GLAPUSH) have undefined paths
 * as they are not clickable menu items, only for grouping
 */
const PATH_MAP: Record<string, string | undefined> = {
  // Main routes (match routes.tsx)
  DASHBOARD: '/',
  USERMGT: '/users',
  INVENTORY: '/inventory',
  SALES: '/sales',
  REPORTS: '/reports',
  SETTINGS: '/settings',

  // FX Position routes
  FXPOSITION: '/fx-position/toolbar',
  FXLIST: '/fx-position/list-items',
  FXGENERATE: '/fx-position/generate',

  // Menu folders (no path - just for grouping)
  MASTER: undefined, // Folder only, no route
  GLAPULL: undefined, // Folder only, no route
  GLAPUSH: undefined, // Folder only, no route

  // Sub-menus under folders (placeholder paths)
  CUSTOMER: '/master/customers',
  SUPPLIER: '/master/suppliers',
  CURRENCY: '/master/currencies',
  COA: '/master/coa',
  GLAPULLCOA: '/gla-pull/coa',
  GLAPULLBAL: '/gla-pull/balance',
  GLAPUSHAP: '/gla-push/ap',
  GLAPUSHAR: '/gla-push/ar',
  GLAPUSHJL: '/gla-push/jl',
  PROFILE: '/settings/profile',
  SYSTEM: '/settings/system',
};

/**
 * Map function ID to icon name
 */
function mapFunctionToIcon(functionId: string | null): string | undefined {
  if (!functionId) {
    return undefined;
  }

  const upperFunId = functionId.toUpperCase();

  // Exact match
  if (ICON_MAP[upperFunId]) {
    return ICON_MAP[upperFunId];
  }

  // Partial match
  for (const [key, icon] of Object.entries(ICON_MAP)) {
    if (upperFunId.includes(key)) {
      return icon;
    }
  }

  return 'AppstoreOutlined'; // Default icon
}

/**
 * Map function ID to route path
 */
function mapFunctionToPath(functionId: string | null): string | undefined {
  if (!functionId) {
    return undefined;
  }

  const upperFunId = functionId.toUpperCase();
  return PATH_MAP[upperFunId] || `/${functionId.toLowerCase()}`;
}

/**
 * Create a menu node from MenuFunctionDto
 */
function createMenuNode(menu: MenuFunctionDto): MenuTreeNode {
  return {
    key: menu.functionId || `node-${menu.nodeId}`,
    nodeId: menu.nodeId,
    label: menu.functionName || 'Unnamed',
    icon: mapFunctionToIcon(menu.functionId),
    path: mapFunctionToPath(menu.functionId),
    order: menu.sequenceNo || 0,
    children: [],
  };
}

/**
 * Validate menu item has required fields
 */
function isValidMenuItem(menu: MenuFunctionDto): boolean {
  return menu !== null && menu !== undefined && typeof menu.nodeId === 'number';
}

/**
 * Add node to parent or root items
 */
function addNodeToHierarchy(
  menu: MenuFunctionDto,
  node: MenuTreeNode,
  menuMap: Map<number, MenuTreeNode>,
  rootItems: MenuTreeNode[],
): void {
  // If parentId is null, it's a root item
  if (menu.parentId === null) {
    rootItems.push(node);
    return;
  }

  // Otherwise, add to parent's children
  const parent = menuMap.get(menu.parentId);
  if (parent) {
    if (!parent.children) {
      parent.children = [];
    }
    parent.children.push(node);
  } else {
    // Parent not found - add to root as fallback
    logger.warn('Parent not found for menu node, adding to root', {
      parentId: menu.parentId,
      nodeId: menu.nodeId,
    });
    rootItems.push(node);
  }
}

/**
 * Build hierarchy by linking nodes to parents
 */
function buildHierarchy(
  menuFunctions: MenuFunctionDto[],
  menuMap: Map<number, MenuTreeNode>,
): MenuTreeNode[] {
  const rootItems: MenuTreeNode[] = [];

  for (const menu of menuFunctions) {
    const node = menuMap.get(menu.nodeId);
    if (!node) {
      continue;
    }

    try {
      addNodeToHierarchy(menu, node, menuMap, rootItems);
    } catch (error) {
      logger.error('Error building hierarchy for menu', error as Error);
    }
  }

  return rootItems;
}

/**
 * Build hierarchical menu tree from flat array
 *
 * Error Handling:
 * - Returns default menu if input is invalid
 * - Catches and logs errors during tree building
 * - Gracefully handles missing or malformed data
 */
export function buildMenuTree(
  menuFunctions: MenuFunctionDto[],
): MenuTreeNode[] {
  try {
    // Validate input
    if (
      !menuFunctions ||
      !Array.isArray(menuFunctions) ||
      menuFunctions.length === 0
    ) {
      logger.warn('No menu data provided, using default menu');
      return getDefaultMenu();
    }

    const menuMap = new Map<number, MenuTreeNode>();

    // First pass: create all nodes
    for (const menu of menuFunctions) {
      if (!isValidMenuItem(menu)) {
        logger.warn('Invalid menu item', { menu });
        continue;
      }

      try {
        menuMap.set(menu.nodeId, createMenuNode(menu));
      } catch (error) {
        logger.error('Error creating node for menu', error as Error);
      }
    }

    // Second pass: build hierarchy
    const rootItems = buildHierarchy(menuFunctions, menuMap);

    // Sort and clean
    const result = sortAndClean(rootItems);

    // If no menu items were created, return default
    if (result.length === 0) {
      logger.warn('No valid menu items built, using default menu');
      return getDefaultMenu();
    }

    return result;
  } catch (error) {
    logger.error('Fatal error building menu tree', error as Error);
    return getDefaultMenu();
  }
}

/**
 * Sort items by order and remove empty children arrays
 * Handles errors gracefully to prevent crashes
 */
function sortAndClean(items: MenuTreeNode[]): MenuTreeNode[] {
  try {
    if (!Array.isArray(items)) {
      logger.warn('sortAndClean: items is not an array');
      return [];
    }

    const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sorted.map(item => {
      try {
        if (item.children && item.children.length > 0) {
          item.children = sortAndClean(item.children);
        } else {
          delete item.children;
        }
        return item;
      } catch (error) {
        logger.error('Error sorting item', error as Error);
        return item;
      }
    });
  } catch (error) {
    logger.error('Error in sortAndClean', error as Error);
    return [];
  }
}

/**
 * Get default menu when database is empty or error occurs
 */
function getDefaultMenu(): MenuTreeNode[] {
  return [
    {
      key: 'DASHBOARD',
      nodeId: 1,
      label: 'Dashboard',
      icon: 'DashboardOutlined',
      path: '/',
      order: 1,
    },
    {
      key: 'USERMGT',
      nodeId: 2,
      label: 'User Management',
      icon: 'UserOutlined',
      path: '/users',
      order: 2,
    },
  ];
}
